import React, { useState } from 'react';
import { arrayOf, number, object } from 'prop-types';
import css from './TesterEarnings.module.css';

// A tester's Earnings statement: total earned, where the money stands, and the
// payout history. This is the one component behind the tester's own Earnings
// page AND the admin's "Tester view" tab, so what the admin sees for a tester is
// exactly what that tester sees — same numbers, same wording.

// How a payout's status reads to the tester (their perspective, not the admin's).
const PAYOUT_STATUS_LABELS = {
  owed: 'Pending',
  paid: 'Paid',
  refunded: 'Cancelled',
  refund_failed: 'On hold',
  clawback_pending: 'Deducted',
};

// Show this many recent payouts before a "Show all" toggle.
const HISTORY_PREVIEW = 10;

const statusBadgeClass = status => {
  if (status === 'paid') return css.badgePaid;
  if (status === 'clawback_pending') return css.badgeDeducted;
  if (status === 'refund_failed') return css.badgeDeducted;
  if (status === 'refunded') return css.badgeRefunded;
  return css.badgePending;
};

const formatMoney = amount =>
  `$${Number(amount || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatHistoryDate = item => {
  const iso =
    item.status === 'paid'
      ? item.paidAt
      : item.status === 'refund_failed'
      ? item.refundFailedAt || item.acceptedAt
      : item.status === 'refunded' || item.status === 'clawback_pending'
      ? item.refundedAt
      : item.acceptedAt;
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString();
};

const TesterEarnings = props => {
  const { totalPaidOutUsd, payoutHistory, ledger } = props;
  const [showAllHistory, setShowAllHistory] = useState(false);

  // The ledger comes back with the payout history; the cached total is the
  // fallback while that request is still in flight.
  const total = ledger ? ledger.totalEarnedUsd : Number(totalPaidOutUsd || 0);
  const adjustments = ledger ? ledger.adjustmentsUsd : 0;
  const adjustmentCount = payoutHistory.filter(item => item.status === 'clawback_pending').length;
  const failedRefundCount = payoutHistory.filter(item => item.status === 'refund_failed').length;
  const visibleHistory = showAllHistory ? payoutHistory : payoutHistory.slice(0, HISTORY_PREVIEW);

  return (
    <>
      <div className={css.earningsCard}>
        <span className={css.earningsLabel}>Total earned</span>
        <span className={css.earningsValue}>{formatMoney(total)}</span>
        <span className={css.earningsHint}>
          From completed tests. This updates once each booking is completed.
        </span>
      </div>

      {ledger && payoutHistory.length > 0 ? (
        <div className={css.breakdown}>
          <div className={css.breakdownRow}>
            <span>Paid out</span>
            <span className={css.breakdownValue}>{formatMoney(ledger.paidOutUsd)}</span>
          </div>
          <div className={css.breakdownRow}>
            <span>Pending payout</span>
            <span className={css.breakdownValue}>{formatMoney(ledger.pendingUsd)}</span>
          </div>
          {adjustments > 0 ? (
            <div className={css.breakdownRow}>
              <span>Refund adjustments</span>
              <span className={css.breakdownNegative}>−{formatMoney(adjustments)}</span>
            </div>
          ) : null}
          <div className={css.breakdownTotal}>
            <span>Next payout</span>
            <span>{formatMoney(ledger.balanceDueUsd)}</span>
          </div>
        </div>
      ) : null}

      {adjustments > 0 ? (
        <p className={css.adjustmentNote}>
          {adjustmentCount > 1
            ? "Tests you'd already been paid for were refunded to the buyer, so "
            : "A test you'd already been paid for was refunded to the buyer, so "}
          {formatMoney(adjustments)} is being deducted from your next payout
          {ledger.adjustmentsCarriedUsd > 0
            ? ` — ${formatMoney(
                ledger.adjustmentsCarriedUsd
              )} of it carries over to the payout after that`
            : ''}
          .
        </p>
      ) : null}

      {failedRefundCount > 0 ? (
        <p className={css.adjustmentNote}>
          {failedRefundCount === 1
            ? 'A cancelled test is on hold while its customer refund is resolved. It is'
            : 'Cancelled tests are on hold while their customer refunds are resolved. They are'}{' '}
          not included in your next payout.
        </p>
      ) : null}

      {payoutHistory.length > 0 ? (
        <div className={css.historySection}>
          <h2 className={css.historyTitle}>Payout history</h2>
          <ul className={css.historyList}>
            {visibleHistory.map(item => (
              <li key={item.transactionId} className={css.historyItem}>
                <div className={css.historyLeft}>
                  {item.status === 'clawback_pending' ? (
                    <span className={css.historyDeduction}>−{formatMoney(item.clawbackUsd)}</span>
                  ) : (
                    <span className={css.historyAmount}>{formatMoney(item.amount)}</span>
                  )}
                  <span className={css.historyListing}>{item.customerName || 'Customer'}</span>
                </div>
                <div className={css.historyRight}>
                  <span className={statusBadgeClass(item.status)}>
                    {PAYOUT_STATUS_LABELS[item.status] || item.status}
                  </span>
                  <span className={css.historyDate}>{formatHistoryDate(item)}</span>
                </div>
              </li>
            ))}
          </ul>
          {payoutHistory.length > HISTORY_PREVIEW ? (
            <button
              type="button"
              className={css.historyToggle}
              onClick={() => setShowAllHistory(value => !value)}
            >
              {showAllHistory ? 'Show less' : `Show all ${payoutHistory.length}`}
            </button>
          ) : null}
        </div>
      ) : (
        <p className={css.emptyText}>
          No payouts yet. Once you complete a test, it'll show up here.
        </p>
      )}
    </>
  );
};

TesterEarnings.defaultProps = {
  totalPaidOutUsd: null,
  payoutHistory: [],
  ledger: null,
};

TesterEarnings.propTypes = {
  totalPaidOutUsd: number,
  payoutHistory: arrayOf(object),
  ledger: object,
};

export default TesterEarnings;
